import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Modal,
  Breadcrumbs,
  Link,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import Navbar from "../components/Navigation";
import Footer from "../components/Footer";
import DocumentsPanel from "../components/DocumentsPanel";
import ModernUploader from "../components/ModernUploader";
import SnackbarAlert from "../components/SnackbarAlert";
import API_ENDPOINTS from "../utilities/APIRoutes";
import useDataManagement from "../hooks/useDataManagement";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.9)",
  },
  centerContent: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: theme.spacing(4, 8),
    marginTop: theme.spacing(8),
    // width: "100%",
    // alignSelf: "center",
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(4),
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  headerSection: {
    marginBottom: theme.spacing(4),
  },
  shareContainer: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    alignItems: "center",
    justifyContent: "space-between",
  },
  breadcrumbLink: {
    display: "flex",
    alignItems: "center",
    color: "#FFC629",
    textDecoration: "none",
    fontFamily: "Raleway, sans-serif",
    cursor: "pointer",
    fontSize: "0.9rem",
    marginBottom: theme.spacing(2),
    "&:hover": {
      textDecoration: "underline",
    },
  },
  collectionName: {
    color: "#ffffff",
    fontWeight: "bold",
    fontFamily: "Raleway, sans-serif",
    display: "flex",
    // alignItems: "center",
    fontSize: "2.3rem",
    gap: theme.spacing(2),
  },
  description: {
    display: "flex",
    color: "#b0b0b0",
    fontFamily: "Raleway, sans-serif",
    fontStyle: "italic",
    marginTop: theme.spacing(1),
    fontSize: "1rem",
  },
  metaInfo: {
    display: "flex",
    color: "#808080",
    fontFamily: "Poppins, sans-serif",
    fontSize: "0.8rem",
    marginTop: theme.spacing(0.5),
  },
  sectionTitle: {
    display: "flex",
    color: "#ffffff",
    fontWeight: "bold",
    fontFamily: "Raleway, sans-serif",
    marginBottom: theme.spacing(2),
    fontSize: "1.5rem",
  },
  yellowButton: {
    backgroundColor: "#FFC629",
    color: "#000000",
    borderRadius: "8px",
    padding: theme.spacing(1, 3),
    fontWeight: "bold",
    textTransform: "none",
    fontFamily: "Raleway, sans-serif",
    "&:hover": {
      backgroundColor: "#FFD700",
    },
  },
  deleteButton: {
    color: "#d32f2f",
    borderColor: "#d32f2f",
    "&:hover": { backgroundColor: "#d32f2f", color: "white" },
  },
  inputField: {
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
    },
  },
  sharedListContainer: {
    marginTop: theme.spacing(2),
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    borderRadius: "8px",
    padding: theme.spacing(1),
  },
  sharedUserItem: {
    backgroundColor: "#333333",
    borderRadius: "8px",
    marginBottom: theme.spacing(1),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  modal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 500,
    backgroundColor: "#202020",
    borderRadius: "14px",
    padding: theme.spacing(4),
    outline: "none",
  },
  modalTitle: {
    textAlign: "center",
    fontFamily: "Raleway, sans-serif",
    fontWeight: "bold",
    color: "white",
    fontSize: "1.5rem",
    marginBottom: theme.spacing(3),
  },
  modalButtonContainer: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(2),
  },
}));

function CollectionDetails() {
  const classes = useStyles();
  const { collectionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // State
  const [collection, setCollection] = useState(
    location.state?.collection || null
  );
  const [loading, setLoading] = useState(false);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [shareEmail, setShareEmail] = useState("");
  const [uploaderOpen, setUploaderOpen] = useState(false);
  const [deleteCollectionOpen, setDeleteCollectionOpen] = useState(false);

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const authToken = localStorage.getItem("authToken");
  const userToken = localStorage.getItem("userToken");

  // Hooks
  const showSnackbar = useCallback((message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);
  
  useEffect(() => {
    if (location.state?.snackbarMessage) {
      showSnackbar(location.state.snackbarMessage, location.state.snackbarSeverity);
      // Optional: Clear state to prevent it showing again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, showSnackbar]);

  const {
    files,
    fetchFiles,
    selectedFile,
    selectedURL,
    getRootProps,
    getInputProps,
    isDragActive,
    error,
    helperText,
    open: deleteFileOpen,
    fileToDelete,
    handleUploadClick,
    removeFile,
    handleUrlChange,
    handleClickOpen: confirmFileDelete,
    handleClose: closeFileDelete,
    handleConfirmDelete,
    setSelectedCollections,
    loading: dataManagementLoading,
    handleDownloadFile,
    downloadingFileIds,
  } = useDataManagement(authToken, userToken, showSnackbar);

  // -- Initial Load --
  useEffect(() => {
    if (collection) {
      setSelectedCollections([collection]);
      fetchFiles([collection.id]);
      fetchSharedUsers(collection.id);
    } else {
      navigate("/collections");
    }
  }, [collection, collectionId, fetchFiles, navigate, setSelectedCollections]);

  // -- Sharing Logic --
  const fetchSharedUsers = async (id) => {
    try {
      const response = await fetch(API_ENDPOINTS.RetrieveCollection, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: authToken,
          "User-Token": userToken,
        },
        body: JSON.stringify({ id }),
      });
      const data = await response.json();
      if (!data.error && data.message?.SharedWith) {
        setSharedUsers(data.message.SharedWith);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleShareCollection = async () => {
    if (!shareEmail.trim()) return showSnackbar("Enter a valid email", "error");
    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.ShareUserCollection, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: authToken,
          "User-Token": userToken,
        },
        body: JSON.stringify({
          user_email: shareEmail,
          collection_id: collection.id,
        }),
      });
      const data = await response.json();
      if (!data.error) {
        setShareEmail("");
        fetchSharedUsers(collection.id);
        showSnackbar("Shared successfully!", "success");
      } else {
        showSnackbar(data.message || "Sharing failed", "error");
      }
    } catch (e) {
      showSnackbar("Error sharing collection", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUnshareUser = async (email) => {
    try {
      const response = await fetch(API_ENDPOINTS.UnshareCollection, {
        method: "POST",
        headers: {
          Authorization: authToken,
          "User-Token": userToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_email: email,
          collection_id: collection.id,
        }),
      });
      const data = await response.json();
      if (!data.error) {
        setSharedUsers((prev) => prev.filter((u) => u !== email));
        showSnackbar("Un-shared successfully!", "success");
      }
    } catch (e) {
      showSnackbar("Error unsharing", "error");
    }
  };

  // -- Delete Collection Logic --
  const handleDeleteCollection = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.DeleteCollectionAI, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "authorization-token": authToken,
          "user-token": userToken,
        },
        body: JSON.stringify({
          collection_id: collection.id,
          collection_name: collection.name,
        }),
      });
      if (response.ok) {
        showSnackbar("Collection deleted successfully!", "success");
        navigate("/collections", {
          state: {
            snackbarMessage: "Collection deleted successfully!",
            snackbarSeverity: "success",
          },
        });
      } else {
        showSnackbar("Failed to delete collection.", "error");
        navigate("/collections", {
          state: {
            snackbarMessage: "Failed to delete collection.",
            snackbarSeverity: "error",
          },
        });
      }
    } catch (err) {
      showSnackbar("Error deleting collection.", "error");
    } finally {
      setLoading(false);
      setDeleteCollectionOpen(false);
    }
  };

  if (!collection) return <Box className={classes.root} />;

  return (
    <Box className={classes.root}>
      <Navbar />
      <Box className={classes.centerContent}>
        {/* Header Section */}
        <Box className={classes.headerSection}>
          <Link
            className={classes.breadcrumbLink}
            onClick={() => navigate("/collections")}
          >
            <ArrowBackIcon style={{ fontSize: "1.2rem", marginRight: 4 }} />
            Back to Collections
          </Link>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={2}
          >
            <Box>
              <Typography variant="h3" className={classes.collectionName}>
                {collection.name}
              </Typography>
              <Typography variant="h6" className={classes.description}>
                {collection.description}
              </Typography>
              <Typography className={classes.metaInfo}>
                Created on {new Date(collection.createdAt).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Documents Section */}
        <Box mb={6}>
          <DocumentsPanel
            files={files}
            handleClickOpen={confirmFileDelete}
            open={deleteFileOpen}
            handleClose={closeFileDelete}
            fileToDelete={fileToDelete}
            handleConfirmDelete={() => handleConfirmDelete(collection.id)}
            loading={dataManagementLoading}
            onAddClick={() => setUploaderOpen(true)}
            onDownload={(file) => handleDownloadFile(file, collection.id)}
            downloadingFileIds={downloadingFileIds}
          />
        </Box>

        {/* Manage User Access Section */}
        <Box style={{ marginBottom: 24, marginTop: 16 }}>
          <Typography className={classes.sectionTitle}>
            Manage User Access
          </Typography>

          <Box style={{ marginBottom: 16 }} >
            {/* <Typography style={{ color: "white", marginBottom: 8, fontFamily: "Raleway, sans-serif", alignSelf: "start" }}>
              Share with
            </Typography> */}
            <Box className={classes.shareContainer}>
              <TextField
                style={{ width: "90%", height: 40 }}
                variant="outlined"
                placeholder="User Email Address"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                className={classes.inputField}
                InputProps={{
                  style: { fontFamily: "Poppins, sans-serif", height: 40 },
                }}
              />
              <Button
                className={classes.yellowButton}
                onClick={handleShareCollection}
                disabled={loading}
                style={{ minWidth: 120, alignItems: "center"}}
              >
                Share
              </Button>
            </Box>
          </Box>

          <Typography style={{ color: "white", fontWeight: "bold", fontFamily: "Raleway, sans-serif", justifySelf: "start" }}>
            Shared With:
          </Typography>
          <Box className={classes.sharedListContainer}>
            {sharedUsers.length > 0 ? (
              <List>
                {sharedUsers.map((u, i) => (
                  <ListItem key={i} className={classes.sharedUserItem}>
                    <ListItemText
                      primary={u}
                      primaryTypographyProps={{
                        style: { color: "white", fontFamily: "Poppins" },
                      }}
                    />
                    <IconButton onClick={() => handleUnshareUser(u)}>
                      <DeleteOutlineOutlinedIcon sx={{ color: "#FFC629" }} />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography style={{ color: "#aaa", padding: 16, fontFamily: "Raleway, sans-serif" }}>
                Not shared with anyone yet.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box>
        <Button
          variant="outlined"
          className={classes.deleteButton}
          onClick={() => setDeleteCollectionOpen(true)}
        >
          Delete Collection
        </Button>
      </Box>
      {/* Uploader Modal */}
      <Modal open={uploaderOpen} onClose={() => setUploaderOpen(false)}>
        <ModernUploader
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          isDragActive={isDragActive}
          files={selectedFile}
          url={selectedURL}
          onRemoveFile={removeFile}
          onUrlChange={handleUrlChange}
          onUpload={async () => {
            await handleUploadClick();
            setUploaderOpen(false);
            fetchFiles([collection.id]);
          }}
          loading={dataManagementLoading}
          error={error}
          helperText={helperText}
          isModal={true}
          onCancel={() => setUploaderOpen(false)}
        />
      </Modal>

      {/* Delete Collection Modal */}
      <Modal
        open={deleteCollectionOpen}
        onClose={() => setDeleteCollectionOpen(false)}
      >
        <Box className={classes.modal}>
          <Typography className={classes.modalTitle}>
            Delete Collection?
          </Typography>
          <Typography
            style={{ color: "#b0b0b0", textAlign: "center", marginBottom: 20, fontFamily: "Poppins, sans-serif" }}
          >
            Are you sure you want to delete{" "}
            <span style={{ color: "#FFC629" }}>{collection.name}</span>? This
            cannot be undone.
          </Typography>
          <Box className={classes.modalButtonContainer}>
            <Button
              onClick={handleDeleteCollection}
              className={classes.yellowButton}
              disabled={loading}
            >
              {loading ? "Deleting..." : "Delete"}
            </Button>
            <Button
              onClick={() => setDeleteCollectionOpen(false)}
              style={{ color: "white" }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      <SnackbarAlert
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
      <Footer />
    </Box>
  );
}

export default CollectionDetails;
